// Game Section Manager - Manage league's game library
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLeague } from '../contexts/LeagueContext';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Input } from '../components';
import {
  createGameSection,
  updateGameSection,
  updateGameSectionPhoto,
  deleteGameSection,
  gameExistsInLeague,
  rateGameSection,
  markGameAsPlayed
} from '../services/gameSectionService';
import { getCategoriesForCompetition } from '../services/categoryService';

// DiceFace component - renders a realistic looking dice
const DiceFace = ({ value, size = 32, selected = false }) => {
  const dotSize = size * 0.18;

  // Dot positions for each dice value
  const dotPositions = {
    1: [[0.5, 0.5]],
    2: [[0.25, 0.25], [0.75, 0.75]],
    3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
    4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
    5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
    6: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.5], [0.75, 0.5], [0.25, 0.75], [0.75, 0.75]]
  };

  const dots = dotPositions[value] || [];

  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: selected ? '#DC2626' : '#EF4444',
        borderRadius: size * 0.2,
        position: 'relative',
        boxShadow: selected
          ? '0 4px 6px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.2)'
          : '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.2)',
        border: selected ? '2px solid #991B1B' : '2px solid #B91C1C'
      }}
    >
      {dots.map((pos, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: dotSize,
            height: dotSize,
            backgroundColor: 'white',
            borderRadius: '50%',
            left: `calc(${pos[0] * 100}% - ${dotSize / 2}px)`,
            top: `calc(${pos[1] * 100}% - ${dotSize / 2}px)`,
            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
          }}
        />
      ))}
    </div>
  );
};

const GameSectionManager = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentLeague,
    leagueGameSections,
    leagueCompetitions,
    isLeagueAdmin,
    loading,
    refreshGameSections,
    loadLeague
  } = useLeague();

  // Ensure league is loaded
  useEffect(() => {
    if (leagueId && (!currentLeague || currentLeague.id !== leagueId)) {
      loadLeague(leagueId);
    }
  }, [leagueId, currentLeague, loadLeague]);

  // Check if user is a member of the league
  const isMember = currentLeague?.memberIds?.includes(user?.uid);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);

  // Form state
  const [gameName, setGameName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [rulesUrl, setRulesUrl] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Rating state
  const [ratingGame, setRatingGame] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [savingRating, setSavingRating] = useState(false);
  const [togglingPlayed, setTogglingPlayed] = useState(null); // Track which game is being toggled

  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  // Categories from competitions in this league
  const [competitionCategories, setCompetitionCategories] = useState([]);

  // Load categories from all league competitions
  useEffect(() => {
    const loadCompetitionCategories = async () => {
      if (!leagueCompetitions || leagueCompetitions.length === 0) return;

      try {
        const allCategories = [];
        for (const comp of leagueCompetitions) {
          const cats = await getCategoriesForCompetition(comp.id);
          allCategories.push(...cats.filter(c => c.status === 'active'));
        }
        setCompetitionCategories(allCategories);
      } catch (err) {
        console.error('Error loading competition categories:', err);
      }
    };
    loadCompetitionCategories();
  }, [leagueCompetitions]);

  // Get unique categories from existing games + competition categories
  const existingLibraryCategories = [...new Set(leagueGameSections.map(g => g.categoryName).filter(Boolean))];
  const competitionCategoryNames = [...new Set(competitionCategories.map(c => c.name))];
  const allCategoryNames = [...new Set([...existingLibraryCategories, ...competitionCategoryNames])].sort();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be less than 5MB');
        return;
      }

      setPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setGameName('');
    setCategoryName('');
    setRulesUrl('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
    setEditingGame(null);
    setShowAddForm(false);
  };

  const handleEdit = (game) => {
    if (!isLeagueAdmin) {
      setError('Only admins can edit games');
      return;
    }
    setEditingGame(game);
    setGameName(game.name);
    setCategoryName(game.categoryName);
    setRulesUrl(game.rulesUrl || '');
    setPhotoPreview(game.photoUrl);
    setShowAddForm(true);
    // Scroll to form after state update
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async () => {
    if (!gameName.trim()) {
      setError('Game name is required');
      return;
    }
    if (!categoryName.trim()) {
      setError('Category is required');
      return;
    }

    // Check for duplicates (only for new games)
    if (!editingGame) {
      const exists = await gameExistsInLeague(leagueId, gameName.trim());
      if (exists) {
        setError(`A game named "${gameName.trim()}" already exists in this league`);
        return;
      }
    }

    setSaving(true);
    setError('');

    try {
      if (editingGame) {
        // Update existing game (admin only)
        await updateGameSection(editingGame.id, {
          name: gameName.trim(),
          categoryName: categoryName.trim(),
          rulesUrl: rulesUrl.trim() || null
        });

        // Upload new photo if changed (admin only)
        if (photoFile && isLeagueAdmin) {
          await updateGameSectionPhoto(editingGame.id, leagueId, photoFile);
        }
        setSuccess('Game updated successfully!');
      } else {
        // Create new game
        // Generate a simple categoryId from categoryName
        const categoryId = categoryName.trim().toLowerCase().replace(/\s+/g, '-');

        // Only pass photo if admin
        await createGameSection(
          leagueId,
          gameName.trim(),
          categoryId,
          categoryName.trim(),
          rulesUrl.trim() || '',
          isLeagueAdmin ? photoFile : null
        );
        setSuccess('Game added successfully!');
      }

      await refreshGameSections();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving game:', err);
      setError('Failed to save game: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (game) => {
    if (!isLeagueAdmin) {
      setError('Only admins can delete games');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${game.name}"?`)) {
      return;
    }

    try {
      await deleteGameSection(game.id, leagueId);
      await refreshGameSections();
      setSuccess('Game deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting game:', err);
      setError('Failed to delete game: ' + err.message);
    }
  };

  const handleRateGame = (game) => {
    setRatingGame(game);
    // Get user's existing rating if any
    const userRating = game.ratings?.[user?.uid] || 0;
    setSelectedRating(userRating);
  };

  const handleSubmitRating = async () => {
    if (!ratingGame || !selectedRating) return;

    setSavingRating(true);
    try {
      await rateGameSection(ratingGame.id, user.uid, selectedRating);
      await refreshGameSections();
      setRatingGame(null);
      setSelectedRating(0);
      setSuccess('Rating saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving rating:', err);
      setError('Failed to save rating: ' + err.message);
    } finally {
      setSavingRating(false);
    }
  };

  const handleTogglePlayed = async (game) => {
    if (!user?.uid) return;

    const hasPlayed = game.playedBy?.includes(user.uid);
    setTogglingPlayed(game.id);

    try {
      await markGameAsPlayed(game.id, user.uid, !hasPlayed);
      await refreshGameSections();
    } catch (err) {
      console.error('Error toggling played status:', err);
      setError('Failed to update: ' + err.message);
    } finally {
      setTogglingPlayed(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isMember && !isLeagueAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">Only league members can view the game library.</p>
          <Button onClick={() => navigate(`/league/${leagueId}`)}>Back to League</Button>
        </Card>
      </div>
    );
  }

  // Group games by category and sort
  const gamesByCategory = leagueGameSections.reduce((acc, game) => {
    const cat = game.categoryName || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(game);
    return acc;
  }, {});

  // Sort categories alphabetically
  const sortedCategories = Object.keys(gamesByCategory).sort();

  // Sort games within each category by name
  sortedCategories.forEach(cat => {
    gamesByCategory[cat].sort((a, b) => a.name.localeCompare(b.name));
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/league/${leagueId}`)}>
              ← Back to League
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">Game Library</h1>
            <p className="text-gray-600">{currentLeague?.name}</p>
          </div>
          {(isMember || isLeagueAdmin) && (
            <Button onClick={() => setShowAddForm(true)}>
              + Add Game
            </Button>
          )}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card ref={formRef} className="mb-6">
            <h2 className="text-xl font-bold mb-4">
              {editingGame ? 'Edit Game' : 'Add New Game'}
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Photo Upload - Only for admins */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Game Photo (1:1 format)
                  {!isLeagueAdmin && <span className="text-gray-400 ml-1">(Admin only)</span>}
                </label>
                {isLeagueAdmin ? (
                  <>
                    <div
                      className="aspect-square bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors overflow-hidden"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center p-4">
                          <div className="text-4xl mb-2">📷</div>
                          <p className="text-sm text-gray-500">Click to upload photo</p>
                          <p className="text-xs text-gray-400 mt-1">Max 5MB, square format recommended</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    {photoPreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview(null);
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove photo
                      </button>
                    )}
                  </>
                ) : (
                  <div className="aspect-square bg-gray-100 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-center p-4">
                      <div className="text-4xl mb-2 opacity-30">📷</div>
                      <p className="text-sm text-gray-400">Photos can only be added by admins</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Game Name *
                  </label>
                  <Input
                    type="text"
                    value={gameName}
                    onChange={(e) => setGameName(e.target.value)}
                    placeholder="e.g., Texas Hold'em"
                    maxLength={50}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  {allCategoryNames.length > 0 ? (
                    <>
                      <select
                        value={allCategoryNames.includes(categoryName) ? categoryName : '__new__'}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setCategoryName('');
                          } else {
                            setCategoryName(e.target.value);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                      >
                        {allCategoryNames.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        <option value="__new__">+ Add new category...</option>
                      </select>
                      {(!allCategoryNames.includes(categoryName)) && (
                        <Input
                          type="text"
                          value={categoryName}
                          onChange={(e) => setCategoryName(e.target.value)}
                          placeholder="Enter new category name"
                          maxLength={30}
                        />
                      )}
                    </>
                  ) : (
                    <Input
                      type="text"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      placeholder="e.g., Poker"
                      maxLength={30}
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Categories from your league competitions are available here
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rules URL (Optional)
                  </label>
                  <Input
                    type="url"
                    value={rulesUrl}
                    onChange={(e) => setRulesUrl(e.target.value)}
                    placeholder="https://example.com/rules"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Link to game rules or instructions
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving...' : (editingGame ? 'Update Game' : 'Add Game')}
                  </Button>
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Rating Modal */}
        {ratingGame && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full">
              <h3 className="text-lg font-bold mb-2">Rate "{ratingGame.name}"</h3>
              <p className="text-sm text-gray-600 mb-4">
                How much do you enjoy this game?
              </p>
              <div className="flex justify-center gap-2 mb-4">
                {[1, 2, 3, 4, 5, 6].map(num => (
                  <button
                    key={num}
                    onClick={() => setSelectedRating(num)}
                    className={`transition-all ${
                      selectedRating === num ? 'scale-110' : 'hover:scale-105'
                    }`}
                    title={`${num} - ${num === 1 ? 'Poor' : num === 2 ? 'Below Average' : num === 3 ? 'Average' : num === 4 ? 'Good' : num === 5 ? 'Great' : 'Excellent'}`}
                  >
                    <DiceFace value={num} size={48} selected={selectedRating === num} />
                  </button>
                ))}
              </div>
              <p className="text-xs text-center text-gray-500 mb-4">
                {selectedRating === 1 && 'Poor'}
                {selectedRating === 2 && 'Below Average'}
                {selectedRating === 3 && 'Average'}
                {selectedRating === 4 && 'Good'}
                {selectedRating === 5 && 'Great'}
                {selectedRating === 6 && 'Excellent!'}
                {!selectedRating && 'Select a rating'}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleSubmitRating}
                  disabled={!selectedRating || savingRating}
                  fullWidth
                >
                  {savingRating ? 'Saving...' : 'Save Rating'}
                </Button>
                <Button variant="outline" onClick={() => setRatingGame(null)}>
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Games List */}
        {leagueGameSections.length === 0 ? (
          <Card className="text-center py-12">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No Games Yet</h3>
            <p className="text-gray-600 mb-4">
              Add games to your library. These will be available when creating competition schedules.
            </p>
            {(isMember || isLeagueAdmin) && (
              <Button onClick={() => setShowAddForm(true)}>Add Your First Game</Button>
            )}
          </Card>
        ) : (
          <div className="space-y-8">
            {sortedCategories.map(category => (
              <div key={category}>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🎲</span> {category}
                  <span className="text-sm font-normal text-gray-500">
                    ({gamesByCategory[category].length} game{gamesByCategory[category].length !== 1 ? 's' : ''})
                  </span>
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {gamesByCategory[category].map(game => (
                    <Card key={game.id} className="overflow-hidden">
                      <div className="aspect-square bg-gray-100 flex items-center justify-center relative group">
                        {game.photoUrl ? (
                          <img
                            src={game.photoUrl}
                            alt={game.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-6xl opacity-30">🎲</span>
                        )}
                        {/* Overlay with actions - only for admin */}
                        {isLeagueAdmin && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button size="sm" onClick={() => handleEdit(game)}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-white"
                              onClick={() => handleDelete(game)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-bold text-gray-900">{game.name}</h4>

                        {/* Rating Display */}
                        <div className="flex items-center gap-2 mt-2">
                          {game.averageRating > 0 ? (
                            <>
                              <DiceFace value={Math.round(game.averageRating)} size={24} />
                              <span className="text-sm font-bold text-gray-700">
                                {game.averageRating.toFixed(1)}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({game.totalRatings || 0})
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">No ratings yet</span>
                          )}
                        </div>

                        {/* Actions: Rate & Played */}
                        {(isMember || isLeagueAdmin) && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleRateGame(game)}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              <DiceFace value={game.ratings?.[user?.uid] || 6} size={16} />
                              <span>{game.ratings?.[user?.uid] ? 'Update' : 'Rate'}</span>
                            </button>
                            <button
                              onClick={() => handleTogglePlayed(game)}
                              disabled={togglingPlayed === game.id}
                              className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${
                                game.playedBy?.includes(user?.uid)
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {togglingPlayed === game.id ? (
                                <span className="animate-spin">...</span>
                              ) : (
                                <>
                                  <span>{game.playedBy?.includes(user?.uid) ? '✓' : '○'}</span>
                                  <span>{game.playedBy?.includes(user?.uid) ? 'Played' : 'Not played'}</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          {game.averageDurationMinutes ? (
                            <span className="text-xs text-gray-500">
                              ~{game.averageDurationMinutes} min avg
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No time data</span>
                          )}
                          {game.rulesUrl && (
                            <a
                              href={game.rulesUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>📖</span> How to Play
                            </a>
                          )}
                        </div>
                        {game.totalTimesPlayed > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            Played {game.totalTimesPlayed} time{game.totalTimesPlayed !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSectionManager;
